/* Mobile Menu Parent Accordion — single-open scoping.
 *
 * Each `_mobile-menu-parent-accordion` block renders a
 * `[data-mobile-parent-accordion]` wrapper around its children. The
 * children are `_mobile-menu-accordion-item` blocks, each a native
 * `<details data-mobile-accordion-item>` element.
 *
 * Native <details> opens / closes independently by default; this
 * script adds a single rule on top: when an accordion item opens,
 * every other open item INSIDE THE SAME parent accordion closes.
 * Items in different parent-accordion instances stay independent.
 *
 * Why not the native `<details name="...">` exclusive-group attr:
 *   That would be the zero-JS solution, but the `name` would have to
 *   be derived from the parent block's id — and accordion-item
 *   blocks don't have access to their parent block's id at Liquid
 *   render time (Shopify's block model isolates them). We'd have to
 *   fall back to section.id, which leaks scope across multiple
 *   parent accordions in one section.
 *
 * Why a document-level listener with capture:
 *   The `toggle` event does NOT bubble, so we can't delegate via the
 *   bubble phase. Listening with `useCapture: true` catches every
 *   toggle that fires on any <details> in the document — no need to
 *   rebind when blocks are added by the customizer at runtime.
 *
 * Nested-parent guard:
 *   `target.closest('[data-mobile-parent-accordion]')` gives the
 *   nearest ancestor parent. For each candidate sibling we re-run
 *   the same closest() and compare — that way an accordion item
 *   that happens to live inside a NESTED parent accordion is not
 *   treated as a sibling of an outer-parent item. The current
 *   schema doesn't allow nesting parent accordions, but the guard
 *   costs nothing and protects against future changes.
 */

document.addEventListener(
  'toggle',
  (event) => {
    const target = event.target;
    if (!(target instanceof HTMLDetailsElement)) return;
    if (!target.hasAttribute('data-mobile-accordion-item')) return;

    if (target.open) {
      // Opening: enforce single-open scoping within the parent
      // accordion. Any other open item inside the same
      // [data-mobile-parent-accordion] collapses so only the
      // just-clicked one stays expanded.
      const parent = target.closest('[data-mobile-parent-accordion]');
      if (!parent) return;

      parent.querySelectorAll('[data-mobile-accordion-item][open]').forEach((sibling) => {
        if (sibling === target) return;
        if (sibling.closest('[data-mobile-parent-accordion]') !== parent) return;
        sibling.removeAttribute('open');
      });
    } else {
      // Closing: reset every nested <details> inside this
      // accordion-item so the next reopen boots to a clean
      // "collapsed sub-category" state. Without this the linklist
      // sub-accordions (native <details> inside the accordion
      // item's panel) persist their open state across a
      // parent-item close/reopen — QA observed this as a stale
      // sub-category staying expanded when the merchant expected
      // a reset.
      //
      // Broad `details[open]` scope covers linklist details plus
      // any future nested <details> component. The listener's own
      // guard (`data-mobile-accordion-item` check) means the
      // toggle events that fire on those children as we clear
      // their `open` attribute are ignored — no cascade risk.
      target.querySelectorAll('details[open]').forEach((nested) => {
        nested.removeAttribute('open');
      });

      // iOS 26 safety net — QA reported on iPhone 17 Pro / iOS
      // 26.5.1 (Chrome + Safari) that closing the parent leaves
      // the nested linklist details' `::details-content` at its
      // pre-close block-size, so the layout still reserves the
      // sub-category's space when the parent is later reopened —
      // even though the `open` attribute was cleared above.
      // Reading a layout property here forces WebKit to commit
      // the pending style change synchronously, so the nested
      // ::details-content actually collapses to block-size: 0
      // before the parent's close animation completes. Void-cast
      // signals to readers that the read is intentional (some
      // linters flag "unused" expressions).
      void target.offsetHeight;
    }
  },
  true,
);
